import React, { useState } from 'react';
import { SlidersHorizontal, Loader2, PackageX, PackagePlus } from 'lucide-react';

const STATUS_BADGE = {
  tracked: { label: 'TRACKED', cls: 'text-green-400 border-green-400/30 bg-green-400/10' },
  untracked: { label: 'NOT TRACKED', cls: 'text-white/40 border-white/15' },
  made_to_order: { label: 'MADE-TO-ORDER', cls: 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10' },
};

// Reorder point, saved on blur (or Enter) — one update per product.
function ThresholdCell({ row, onSave }) {
  const [value, setValue] = useState(row.threshold ?? '');
  const [saving, setSaving] = useState(false);
  const disabled = row.status !== 'tracked';

  const commit = async () => {
    const next = value === '' ? null : Number(value);
    if (next === (row.threshold ?? null)) return;
    setSaving(true);
    await onSave(row.product, next);
    setSaving(false);
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min="0"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder="—"
        className="seo-input w-16 text-center disabled:opacity-30"
      />
      {saving && <Loader2 className="w-3 h-3 animate-spin text-white/30" />}
    </div>
  );
}

const Qty = ({ value, className = 'text-white' }) => (
  <span className={className}>{value == null ? '—' : value}</span>
);

const money = (v) => (v == null ? '—' : `£${Number(v).toFixed(2)}`);

// Thin or negative margins stand out against the admin's dark palette.
const marginTone = (pct) =>
  pct == null ? 'text-white/25'
    : pct < 0 ? 'text-red-400 font-bold'
    : pct < 20 ? 'text-amber-400 font-bold'
    : 'text-green-400 font-bold';

export default function StockInventoryTable({ rows, onAdjust, onReceive, onSaveThreshold }) {
  if (!rows.length) {
    return (
      <div className="border border-white/10 bg-[#111115] p-12 text-center text-white/30">
        <PackageX className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-xs font-bold tracking-widest">NO ITEMS MATCH THIS VIEW</p>
        <p className="text-[11px] mt-1">Try a different filter or clear the search</p>
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-[#111115] overflow-x-auto">
      <table className="w-full text-xs min-w-[1200px]">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-white/30 font-bold text-left py-3 px-4">Product</th>
            <th className="text-white/30 font-bold text-left py-3 px-3">SKU</th>
            <th className="text-white/30 font-bold text-right py-3 px-3">Square On-Hand</th>
            <th className="text-white/30 font-bold text-right py-3 px-3">Allocated (B2B)</th>
            <th className="text-white/30 font-bold text-right py-3 px-3">Net Available</th>
            <th className="text-white/30 font-bold text-right py-3 px-3">Cost (ex VAT)</th>
            <th className="text-white/30 font-bold text-right py-3 px-3">Sell (ex VAT)</th>
            <th className="text-white/30 font-bold text-right py-3 px-3">Margin</th>
            <th className="text-white/30 font-bold text-center py-3 px-3">Reorder Point</th>
            <th className="text-white/30 font-bold text-left py-3 px-3">Tracking</th>
            <th className="py-3 px-4" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const badge = STATUS_BADGE[row.status] || STATUS_BADGE.untracked;
            const rowCls = row.isOut
              ? 'bg-red-400/[0.06] border-l-2 border-l-red-400'
              : row.isLow
                ? 'bg-amber-400/[0.06] border-l-2 border-l-amber-400'
                : 'border-l-2 border-l-transparent';
            return (
              <tr key={row.product.id} className={`border-b border-white/5 ${rowCls}`}>
                <td className="py-3 px-4">
                  <p className="text-white font-bold">{row.product.name}</p>
                  {(row.product.variation_name || row.product.option_value) && (
                    <p className="text-white/40 text-[11px] mt-0.5">
                      {row.product.variation_name || row.product.option_value}
                    </p>
                  )}
                </td>
                <td className="py-3 px-3 text-white/60 font-mono">{row.product.sku || '—'}</td>
                <td className="py-3 px-3 text-right">
                  <Qty
                    value={row.onHand}
                    className={row.isOut ? 'text-red-400 font-bold' : row.isLow ? 'text-amber-400 font-bold' : 'text-white font-bold'}
                  />
                </td>
                <td className="py-3 px-3 text-right">
                  <Qty value={row.committed || null} className={row.committed ? 'text-cyan-400' : 'text-white/25'} />
                </td>
                <td className="py-3 px-3 text-right">
                  <Qty
                    value={row.available}
                    className={row.available != null && row.available <= 0 ? 'text-red-400 font-bold' : 'text-white'}
                  />
                </td>
                <td className="py-3 px-3 text-right text-white/60">{money(row.cost)}</td>
                <td className="py-3 px-3 text-right text-white/60">{money(row.exVat)}</td>
                <td className="py-3 px-3 text-right">
                  {row.margin == null ? (
                    <span className="text-white/25">—</span>
                  ) : (
                    <div className="leading-tight">
                      <p className={marginTone(row.marginPct)}>{money(row.margin)}</p>
                      <p className={`text-[10px] ${marginTone(row.marginPct)}`}>
                        {row.marginPct == null ? '—' : `${row.marginPct.toFixed(0)}%`}
                      </p>
                    </div>
                  )}
                </td>
                <td className="py-3 px-3">
                  <div className="flex justify-center">
                    <ThresholdCell row={row} onSave={onSaveThreshold} />
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] font-bold tracking-widest ${badge.cls}`}>
                    {badge.label}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onAdjust(row)}
                      disabled={row.status !== 'tracked'}
                      title={row.status === 'tracked' ? 'Adjust the Square stock level' : 'Not stock-tracked in Square'}
                      className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-30 disabled:hover:bg-primary/10 disabled:hover:text-primary"
                    >
                      <SlidersHorizontal className="w-3.5 h-3.5" /> ADJUST
                    </button>
                    <button
                      onClick={() => onReceive(row)}
                      disabled={row.status !== 'tracked'}
                      title={row.status === 'tracked' ? 'Book a delivery in against Square' : 'Not stock-tracked in Square'}
                      className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-white/60 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-30 disabled:hover:border-white/15 disabled:hover:text-white/60"
                    >
                      <PackagePlus className="w-3.5 h-3.5" /> RECEIVE
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}